import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";

const GlobalStyles = () => (
    <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    ::selection { background: #bfdbfe; color: #1e3a5f; }

    body { background: #fff; }

    .font-lora { font-family: 'Lora', Georgia, serif; }
    .font-dm   { font-family: 'DM Sans', system-ui, sans-serif; }

    .blog-prose {
      font-family: 'Lora', Georgia, serif;
      font-size: 1.125rem;
      line-height: 1.85;
      color: #1f2937;
    }
    .blog-prose p {
      margin-bottom: 1.6em;
    }
    .blog-prose ul {
        margin-bottom: 1.6em;
        padding-left: 1.5em;
        list-style-type: disc;
    }
    .blog-prose ol {
        margin-bottom: 1.6em;
        padding-left: 1.5em;
        list-style-type: decimal;
    }
    .blog-prose strong {
      font-weight: 600;
      color: #111827;
    }
    .blog-prose a {
      color: #2563eb;
      text-decoration: underline;
      text-underline-offset: 3px;
    }
    .blog-prose a:hover {
      color: #1d4ed8;
    }

    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .fade-in-up { animation: fadeInUp 0.6s ease both; }
    .delay-1 { animation-delay: 0.1s; }
    .delay-2 { animation-delay: 0.2s; }
    .delay-3 { animation-delay: 0.3s; }
    .delay-4 { animation-delay: 0.4s; }

  `}</style>
);

function useScrollReveal() {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setVisible(true); },
            { threshold: 0.1 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);
    return [ref, visible];
}

function FadeIn({ children, delay = 0, className = "" }) {
    const [ref, visible] = useScrollReveal();
    return (
        <div
            ref={ref}
            className={className}
            style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(18px)",
                transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
            }}
        >
            {children}
        </div>
    );
}

function Callout({ label, children }) {
    return (
        <FadeIn className="my-10">
            <div className="rounded-xl border border-blue-100 bg-blue-50 px-6 py-5">
                <p className="font-dm text-xs font-semibold tracking-widest uppercase text-blue-500 mb-2">{label}</p>
                <div className="font-lora text-base text-slate-700 leading-relaxed">{children}</div>
            </div>
        </FadeIn>
    );
}

function Tag({ label }) {
    return (
        <span className="font-dm inline-block text-xs font-medium tracking-wide px-3 py-1 rounded-full bg-slate-100 text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors cursor-default mr-2 mb-2">
            {label}
        </span>
    );
}

export default function GeoAISeminar() {
    const [readProgress, setReadProgress] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            const doc = document.documentElement;
            const scrolled = doc.scrollTop;
            const total = doc.scrollHeight - doc.clientHeight;
            setReadProgress(total > 0 ? Math.min(100, (scrolled / total) * 100) : 0);
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const tags = ["GeoAI", "Artificial Intelligence", "Geospatial", "Sustainable Development", "Digital Transformation", "Seminar"];

    return (
        <>
            <Helmet>
                <title>International Seminar and Training on Geospatial Artificial Intelligence (GeoAI)</title>
                <meta name="description" content="Join the International Seminar and Training on Geospatial Artificial Intelligence (GeoAI) at JKUAT Main Campus. Theme: Harnessing GeoAI for Sustainable Development, Innovation and Digital Transformation." />
            </Helmet>

            <div className="bg-white min-h-screen font-dm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                <GlobalStyles />

                {/* Reading progress bar */}
                <div className="fixed top-[112px] left-0 right-0 z-40 h-[3px] bg-slate-100">
                    <div
                        className="h-full bg-blue-500 origin-left transition-all duration-150"
                        style={{ transform: `scaleX(${readProgress / 100})` }}
                    />
                </div>

                {/* ── HERO / Cover Image ── */}
                <div className="w-full bg-slate-900 relative overflow-hidden mt-[112px]
                h-[45vw]
                max-h-[600px]
                min-h-[350px]">
                    {/* Subtle texture */}
                    <div className="absolute inset-0" style={{
                        backgroundImage: "radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)",
                        backgroundSize: "28px 28px",
                    }} />
                    <div className="absolute inset-0 bg-gradient-to-tr from-blue-900 to-slate-900 opacity-90" />

                    {/* Category badge */}
                    <div className="absolute top-8 left-8">
                        <span className="fade-in font-dm text-xs font-semibold tracking-widest uppercase bg-blue-500 text-white px-3 py-1.5 rounded-full">
                            Upcoming Event
                        </span>
                    </div>

                    {/* Hero title overlay */}
                    <div className="absolute bottom-0 left-0 right-0 px-8 pb-10 max-w-4xl mx-auto text-center md:text-left">
                        <h1 className="fade-in delay-1 font-lora text-white leading-tight mb-4"
                            style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)", fontWeight: 700 }}>
                            International Seminar and Training on Geospatial Artificial Intelligence (GeoAI)
                        </h1>
                        <p className="fade-in delay-2 font-dm text-blue-200 text-lg md:text-xl max-w-2xl">
                            Theme: Harnessing Geospatial Artificial Intelligence for Sustainable Development, Innovation and Digital Transformation
                        </p>
                    </div>
                </div>

                {/* ── ARTICLE WRAPPER ── */}
                <div className="max-w-4xl lg:max-w-6xl mx-auto px-5 sm:px-6">

                    {/* Meta row */}
                    <div className="fade-in delay-2 flex items-center justify-between flex-wrap gap-4 py-6 border-b border-slate-100">
                        <div className="flex items-center gap-6 flex-wrap">
                            <div className="flex flex-col">
                                <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Date</span>
                                <span className="text-sm text-slate-800 font-medium">17-19 August 2026 (3 Days)</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Venue</span>
                                <span className="text-sm text-slate-800 font-medium">JKUAT Main Campus, Juja</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Method</span>
                                <span className="text-sm text-slate-800 font-medium">Hybrid (Online & Offline)</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Language</span>
                                <span className="text-sm text-slate-800 font-medium">English</span>
                            </div>
                        </div>
                    </div>

                    {/* Tags */}
                    <div className="fade-in delay-3 pt-5 pb-2">
                        {tags.map(t => <Tag key={t} label={t} />)}
                    </div>

                    {/* Register Button */}
                    <div className="fade-in delay-4 py-4">
                        <a
                            href="https://forms.gle/QksmooPTYfUgvRCG9"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-8 py-3 text-base font-bold font-dm rounded-full text-white bg-blue-600 hover:bg-blue-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                        >
                            Register Here
                        </a>
                    </div>

                    {/* ── ARTICLE BODY ── */}
                    <article className="pt-8 pb-24">

                        <FadeIn>
                            <h2 className="font-lora font-bold text-slate-900 mt-8 mb-5" style={{ fontSize: "clamp(1.35rem, 2.5vw, 1.65rem)" }}>
                                Background
                            </h2>
                            <div className="blog-prose">
                                <p>
                                    The rapid advancement of Artificial Intelligence (AI), machine learning, remote sensing, and geospatial technologies is transforming how governments, industries, and research institutions collect, analyze, and utilize spatial data. Geospatial Artificial Intelligence (GeoAI) integrates AI with Geographic Information Systems (GIS), Earth Observation, surveying, mapping, and geospatial information science to provide intelligent solutions for urban planning, agriculture, disaster risk management, environmental monitoring, transportation, public health, infrastructure development, and smart cities.
                                </p>
                                <p>
                                    There is an increasing demand for professionals equipped with GeoAI knowledge and practical skills. This seminar and hands-on training aims to strengthen capacity, promote research collaboration, showcase emerging technologies, and facilitate knowledge exchange among academia, industry, government, and students.
                                </p>
                                <p>
                                    This seminar's main organizer and host is the <strong>JKUAT Artificial Intelligence Centre of Excellence for Capacity Building in Africa (AICEX-Africa)</strong>, a member of the United Nations Artificial Intelligence Capacity Development Network-UN AI CDN. Collaborating organizers for the seminar include: World Geospatial Information Home (WGIH)/Deqing Association for Geospatial Information Professionals, Africa Institute of Capacity Development (AICAD), Ningbo University and UNESCO/IOC.
                                </p>
                            </div>
                        </FadeIn>

                        <FadeIn>
                            <h2 className="font-lora font-bold text-slate-900 mt-12 mb-5" style={{ fontSize: "clamp(1.35rem, 2.5vw, 1.65rem)" }}>
                                Objectives
                            </h2>
                            <div className="blog-prose">
                                <p>The seminar seeks to:</p>
                                <ol>
                                    <li>Build capacity in Geospatial Artificial Intelligence and emerging geospatial technologies.</li>
                                    <li>Showcase current GeoAI applications in research, government, and industry.</li>
                                    <li>Promote collaboration among universities, research institutions, industry, and government agencies.</li>
                                    <li>Introduce participants to AI-powered geospatial analytics, spatial data science, and machine learning applications.</li>
                                    <li>Sensitize on ethics, policy, and governance instruments related to GeoAI.</li>
                                </ol>
                            </div>
                        </FadeIn>

                        <FadeIn>
                            <h2 className="font-lora font-bold text-slate-900 mt-12 mb-5" style={{ fontSize: "clamp(1.35rem, 2.5vw, 1.65rem)" }}>
                                Target Participants
                            </h2>
                            <div className="blog-prose">
                                <p>
                                    The seminar targets: Academia, researchers, students, industries, government departments and professionals.
                                </p>
                            </div>
                        </FadeIn>

                        <FadeIn>
                            <h2 className="font-lora font-bold text-slate-900 mt-12 mb-5" style={{ fontSize: "clamp(1.35rem, 2.5vw, 1.65rem)" }}>
                                Training Methodology
                            </h2>
                            <div className="blog-prose">
                                <p>The programme will combine:</p>
                                <ul>
                                    <li><strong>Keynote presentations</strong> - Hybrid (Online & In-person)</li>
                                    <li><strong>Expert panel discussions</strong> - Hybrid (Online & In-person)</li>
                                    <li><strong>Technical paper presentations</strong> - Hybrid (Online & In-person)</li>
                                    <li><strong>Live software demonstrations</strong> - In-person</li>
                                    <li><strong>Hands-on practical training sessions</strong> - In-person</li>
                                    <li><strong>Networking and collaboration forums</strong> - In-person</li>
                                </ul>
                            </div>
                        </FadeIn>

                        <FadeIn>
                            <h2 className="font-lora font-bold text-slate-900 mt-12 mb-5" style={{ fontSize: "clamp(1.35rem, 2.5vw, 1.65rem)" }}>
                                Expected Outcomes
                            </h2>
                            <div className="blog-prose">
                                <p>Participants will:</p>
                                <ul>
                                    <li>Gain practical knowledge and skills in GeoAI tools and techniques.</li>
                                    <li>Understand current trends and applications of AI in geospatial sciences.</li>
                                    <li>Establish research and industry collaborations.</li>
                                    <li>Identify opportunities for innovation, entrepreneurship, and technology transfer.</li>
                                    <li>Receive a Certificate of Participation/Training.</li>
                                </ul>
                            </div>
                        </FadeIn>

                        <Callout label="Participation & Registration">
                            <p><strong>Online participant fee:</strong> FREE.</p>
                            <p className="mt-2">For In-person attendance facilitation details, please contact via email to <strong>Prof Muliaro Wafula</strong> at <a href="mailto:muliaro@icsit.jkuat.ac.ke">muliaro@icsit.jkuat.ac.ke</a>.</p>
                            <div className="mt-5">
                                <a
                                    href="https://forms.gle/QksmooPTYfUgvRCG9"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center px-6 py-2.5 text-sm font-bold font-dm rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-all shadow-sm"
                                >
                                    Complete Registration Form
                                </a>
                            </div>
                        </Callout>

                        <FadeIn>
                            <h2 className="font-lora font-bold text-slate-900 mt-12 mb-5" style={{ fontSize: "clamp(1.35rem, 2.5vw, 1.65rem)" }}>
                                Contacts of Co-Chairs of organizing committee
                            </h2>
                            <div className="blog-prose flex flex-col md:flex-row gap-8">
                                <div className="flex-1 bg-slate-50 p-6 rounded-xl border border-slate-100">
                                    <h3 className="font-bold text-lg mb-2">Prof Muliaro Wafula</h3>
                                    <p className="text-sm text-slate-600 mb-2">JKUAT</p>
                                    <p className="text-sm"><a href="mailto:muliaro@icsit.jkuat.ac.ke">muliaro@icsit.jkuat.ac.ke</a></p>
                                </div>
                                <div className="flex-1 bg-slate-50 p-6 rounded-xl border border-slate-100">
                                    <h3 className="font-bold text-lg mb-2">Hans Ni</h3>
                                    <p className="text-sm text-slate-600 mb-2">CEO Deqing iSpatial Co., Ltd<br />Deqing Association for Geospatial Information Professionals</p>
                                    <p className="text-sm mb-1"><a href="mailto:ni.han@qq.com">ni.han@qq.com</a></p>
                                    <p className="text-sm text-slate-600">Tel/Fax: +86-572-8880975<br />Mobile: +86-13911913997</p>
                                </div>
                            </div>
                        </FadeIn>

                    </article>
                </div>
            </div>
        </>
    );
}
